import React, { useMemo, useState, useCallback } from 'react';
import {
  useLoadEmailMigrationQuery,
  useLoadAllClaudeSpendQuery,
} from '../app/api';
import PropTypes from 'prop-types';
import { Row, Col, Card, ProgressBar } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import UsageTierChart from './UsageTierChart';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const COMPANY_SHORT = {
  'oneteamfinancial':      'OTF',
  'hammerfinancial':       'Hammer',
  'hammerfinancialgroup':  'Hammer',
  'fscwealthadvisors':     'FSC',
};
const MAILBOX_FIRMS = new Set(['OTF', 'Hammer', 'FSC']);
const shortCompany = (c) => {
  if (!c) return '';
  const key = c.toLowerCase().replace(/[^a-z0-9]/g, '');
  return COMPANY_SHORT[key] || c;
};
const effectiveType = (r) =>
  r.migration_type || (MAILBOX_FIRMS.has(shortCompany(r.prior_company)) ? 'mailbox' : 'alias');

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// "2026-07" -> "Jul 2026"
const formatMonthLabel = (ym) => {
  const [y, m] = (ym || '').split('-');
  const idx = Number(m) - 1;
  return idx >= 0 && idx < 12 ? `${MONTHS[idx]} ${y}` : ym;
};

// The Claude adoption buckets the team defined (mirrors the Executive AI page):
// certification progress and live usage. Summarized here with headcounts.
const CERT_CATEGORIES = [
  { key: 'completed',   label: 'Certified',    icon: 'check-circle-fill', color: brand.green },
  { key: 'pending',     label: 'Pending',      icon: 'hourglass-split',   color: brand.goldenrod },
  { key: 'not_started', label: 'Not Started',  icon: 'dash-circle',       color: brand.charcoal },
];
const USAGE_CATEGORIES = [
  { key: 'using',      label: 'Active',   icon: 'check-circle-fill', color: brand.tealBlue },
  { key: 'no_account', label: 'No Usage', icon: 'x-circle',          color: brand.red },
];

// Renders nothing — fetches one month's spend and reports its total up to the
// parent. One probe per month keeps the RTK Query hook count stable while still
// letting us assemble a multi-month series client-side.
function MonthSpendProbe({ month, onTotal }) {
  const { data } = useLoadAllClaudeSpendQuery({ month });
  React.useEffect(() => {
    if (data && data.configured !== false && Array.isArray(data.employees)) {
      const total = data.employees.reduce((s, e) => s + (e.total_cost_usd ?? e.cost_usd ?? 0), 0);
      onTotal(month, total);
    }
  }, [data, month, onTotal]);
  return null;
}
MonthSpendProbe.propTypes = { month: PropTypes.string, onTotal: PropTypes.func };

function CategoryTile({ label, icon, color, count }) {
  return (
    <div style={{ flex: '1 1 130px', background: '#fafaf7', borderRadius: 10, borderLeft: `4px solid ${color}`, padding: '0.85rem 1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        <i className={`bi bi-${icon}`} />{label}
      </div>
      <div style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '1.75rem', lineHeight: 1.1, marginTop: 4 }}>{count}</div>
    </div>
  );
}
CategoryTile.propTypes = { label: PropTypes.string, icon: PropTypes.string, color: PropTypes.string, count: PropTypes.number };

export default function Executive() {
  const { data: emailRows = [] } = useLoadEmailMigrationQuery();

  // Trailing 6 months ending with the current month, oldest first. Computed
  // once so the probe list (and therefore the hook count) stays constant.
  const recentMonths = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      arr.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return arr;
  }, []);

  const [monthlyTotals, setMonthlyTotals] = useState({});
  const handleMonthTotal = useCallback((month, total) => {
    setMonthlyTotals(prev => (prev[month] === total ? prev : { ...prev, [month]: total }));
  }, []);

  // Category counts use the trailing 30-day window (no month arg) rather than
  // the calendar month, so a recently-active user still counts as "Active"
  // right after a month rollover instead of everyone dropping to "No Usage".
  const { data: claudeData } = useLoadAllClaudeSpendQuery({});
  const claudeConfigured = claudeData?.configured;
  const claudeEmployees = useMemo(() => claudeData?.employees || [], [claudeData]);

  const categoryCounts = useMemo(() => {
    const cert = { completed: 0, pending: 0, not_started: 0 };
    const usage = { using: 0, no_account: 0 };
    claudeEmployees.forEach(e => {
      const c = e.cert_status || 'not_started';
      if (c in cert) cert[c] += 1;
      const u = e.account_status;
      if (u in usage) usage[u] += 1;
    });
    return { cert, usage, total: claudeEmployees.length };
  }, [claudeEmployees]);

  const emailMigration = useMemo(() => {
    const out = { total: emailRows.length, notStarted: 0, inProgress: 0, complete: 0 };
    emailRows.forEach(r => {
      const s = r.status || 'Not Started';
      if (s === 'Complete') out.complete += 1;
      else if (s === 'In Progress') out.inProgress += 1;
      else out.notStarted += 1;
    });
    return out;
  }, [emailRows]);
  const emailPct = emailMigration.total ? Math.round((emailMigration.complete / emailMigration.total) * 100) : 0;

  const chartData = useMemo(() => ({
    labels: recentMonths.map(formatMonthLabel),
    datasets: [{
      label: 'Claude spend (USD)',
      data: recentMonths.map(m => monthlyTotals[m] ?? 0),
      borderColor: brand.tealBlue,
      backgroundColor: 'rgba(0,105,166,0.12)',
      pointBackgroundColor: brand.tealBlue,
      pointBorderColor: 'white',
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2,
      tension: 0.3,
      fill: true,
    }],
  }), [recentMonths, monthlyTotals]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => usd(ctx.parsed.y) } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: brand.charcoal } },
      y: {
        beginAtZero: true,
        ticks: { color: brand.charcoal, callback: (v) => `$${Number(v).toLocaleString()}` },
        grid: { color: brand.softGray },
      },
    },
  }), []);

  const anyMonthlyData = recentMonths.some(m => (monthlyTotals[m] ?? 0) > 0);

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>

      {/* Hidden probes — one per month — that feed the monthly chart. */}
      {recentMonths.map(m => (
        <MonthSpendProbe key={m} month={m} onTotal={handleMonthTotal} />
      ))}

      {/* Page header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>Executive Dashboard</h4>
        <p style={{ color: brand.charcoal, margin: 0, fontSize: '0.875rem' }}>Email migration and Claude adoption</p>
      </div>

      {/* Email migration progress */}
      <Row className="g-4 mb-4">
        <Col>
          <Card style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px', overflow: 'hidden' }}>
            <Card.Header style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Card.Title style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Email Migration Progress</Card.Title>
              <span style={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>{emailPct}% complete</span>
            </Card.Header>
            <Card.Body>
              {/* Overall summary strip */}
              <div className="d-flex justify-content-between flex-wrap mb-3" style={{ fontSize: '0.85rem', color: brand.charcoal, gap: '0.5rem' }}>
                <span>Not Started: <b style={{ color: brand.deepBlue }}>{emailMigration.notStarted}</b></span>
                <span>In Progress: <b style={{ color: brand.deepBlue }}>{emailMigration.inProgress}</b></span>
                <span>Complete: <b style={{ color: brand.deepBlue }}>{emailMigration.complete}</b></span>
                <span>Total: <b style={{ color: brand.deepBlue }}>{emailMigration.total}</b></span>
              </div>
              <Row>
                {['alias', 'mailbox'].map(t => {
                  const subset = emailRows.filter(r => effectiveType(r) === t);
                  const total = subset.length;
                  const complete = subset.filter(r => (r.status || 'Not Started') === 'Complete').length;
                  const inProgress = subset.filter(r => r.status === 'In Progress').length;
                  const notStarted = total - complete - inProgress;
                  const p = total ? Math.round((complete / total) * 100) : 0;
                  const label = t === 'alias' ? 'Alias' : 'New Mailbox';
                  return (
                    <Col key={t} md={6}>
                      <div style={{ padding: '0.75rem 1rem', background: '#fafaf7', borderRadius: 8, borderLeft: `4px solid ${brand.goldenrod}` }}>
                        <div className="d-flex justify-content-between align-items-baseline mb-2">
                          <div style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.85rem' }}>{label}</div>
                          <div style={{ color: brand.deepBlue, fontWeight: 800 }}>{p}%</div>
                        </div>
                        <ProgressBar now={p} style={{ height: '6px', backgroundColor: brand.softGray, marginBottom: '0.5rem' }}>
                          <div style={{ width: `${p}%`, background: '#2e7d32', borderRadius: '3px' }} />
                        </ProgressBar>
                        <div className="d-flex justify-content-between" style={{ fontSize: '0.78rem', color: brand.charcoal }}>
                          <span>Not Started: <b>{notStarted}</b></span>
                          <span>In Progress: <b>{inProgress}</b></span>
                          <span>Complete: <b>{complete}</b></span>
                          <span>Total: <b>{total}</b></span>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Claude usage per month */}
      <Row className="g-4 mb-4">
        <Col>
          <Card style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px', overflow: 'hidden' }}>
            <Card.Header style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem' }}>
              <Card.Title style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Claude Usage per Month</Card.Title>
            </Card.Header>
            <Card.Body>
              {claudeConfigured === false ? (
                <div style={{ color: brand.charcoal, fontSize: '0.85rem' }}>
                  <i className="bi bi-info-circle me-1" style={{ color: brand.goldenrod }} />
                  Claude usage reporting isn&apos;t configured yet — an admin needs to set the Anthropic Analytics API key.
                </div>
              ) : !anyMonthlyData ? (
                <div style={{ color: brand.charcoal, fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>
                  <div className="spinner-border spinner-border-sm me-2" style={{ color: brand.tealBlue }} />
                  Loading monthly usage…
                </div>
              ) : (
                <div style={{ height: 300 }}>
                  <Line data={chartData} options={chartOptions} />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Claude adoption categories */}
      <Row className="g-4 mb-4">
        <Col>
          <Card style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px', overflow: 'hidden' }}>
            <Card.Header style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Card.Title style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Claude Adoption Categories</Card.Title>
              <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>{categoryCounts.total} employees · Last 30 days</span>
            </Card.Header>
            <Card.Body>
              {claudeConfigured === false ? (
                <div style={{ color: brand.charcoal, fontSize: '0.85rem' }}>
                  <i className="bi bi-info-circle me-1" style={{ color: brand.goldenrod }} />
                  Claude reporting isn&apos;t configured yet.
                </div>
              ) : (
                <Row className="g-4">
                  <Col md={6}>
                    <div style={{ color: brand.charcoal, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.6rem' }}>Certification</div>
                    <div className="d-flex flex-wrap" style={{ gap: '0.6rem' }}>
                      {CERT_CATEGORIES.map(c => (
                        <CategoryTile key={c.key} label={c.label} icon={c.icon} color={c.color} count={categoryCounts.cert[c.key]} />
                      ))}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div style={{ color: brand.charcoal, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.6rem' }}>Usage (last 30 days)</div>
                    <div className="d-flex flex-wrap" style={{ gap: '0.6rem' }}>
                      {USAGE_CATEGORIES.map(c => (
                        <CategoryTile key={c.key} label={c.label} icon={c.icon} color={c.color} count={categoryCounts.usage[c.key]} />
                      ))}
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Usage distribution — engagement tiers by 30-day activity */}
      {claudeConfigured !== false && claudeEmployees.length > 0 && (
        <Row className="g-4 mb-4">
          <Col>
            <UsageTierChart
              counts={claudeEmployees.map(e => e.activity_30d || 0)}
              title="Usage Distribution"
              subtitle="Employees grouped into engagement tiers by activity (messages + Claude Code sessions) in the last 30 days."
            />
          </Col>
        </Row>
      )}
    </div>
  );
}
